import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LuCheck, LuX } from 'react-icons/lu';
import { elliottTokens as t } from '../tokens';
import { messageBodySx } from './ElliottMessage';
import StreamingText from './StreamingText';
import TypingDots from './TypingDots';

// Options are served WITHOUT correct flags — grading is server-side (elliott-quiz-check).
export interface QuizOption {
  id: string;
  label: string;
}

// Result of the server-side check for one answered question.
export interface QuizCheckResult {
  /** Whether the learner's selected option was correct */
  selectedCorrect: boolean;
  /** Option id of the correct answer (revealed after answering); null if unmatchable or withheld */
  correctOptionId: string | null;
  /** Explainer for the correct answer (empty while withheld) */
  explanation: string;
  /**
   * Withheld-answer mode (rationale-enabled programs, L-1): the answer was
   * wrong and the BE deliberately sent no reveal fields — feedback comes from
   * `chosenReasoning` alone and nothing may hint at any other option.
   */
  withheld?: boolean;
  /** Rationale for the chosen option — the only feedback on a withheld wrong answer */
  chosenReasoning?: string;
  /** One rationale per option, aligned with the served options by index. Empty
   *  while withheld and for older questions that predate the feature. */
  optionReasonings?: string[];
}

// Quiz-specific palette (per Figma MCQ frame).
const FIELD_BG = '#0D0D0F';
const OPTION_TEXT = '#E5E7EB';
const CORRECT_BG = '#061A0D';
const CORRECT_ACCENT = '#00E56A';
const INCORRECT_BG = '#1A0808';
const INCORRECT_ACCENT = '#EF4444';
const EXPLAINER_TEXT = '#9CA3AF';

const fadeIn = {
  animation: 'elliottFadeIn 0.35s ease',
  '@keyframes elliottFadeIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1 } }
} as const;

type OptionState = 'default' | 'correct' | 'incorrect';

const Indicator: React.FC<{ state: OptionState; size?: number }> = ({ state, size = 24 }) => {
  if (state === 'correct') {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: CORRECT_ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <LuCheck size={(size * 2) / 3} color="#fff" strokeWidth={3} />
      </Box>
    );
  }
  if (state === 'incorrect') {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: INCORRECT_ACCENT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <LuX size={(size * 2) / 3} color="#fff" strokeWidth={3} />
      </Box>
    );
  }
  return <Box sx={{ width: size, height: size, borderRadius: '50%', border: `1px solid ${t.borderDim}`, flexShrink: 0 }} />;
};

interface QuizCardProps {
  question: string;
  options: QuizOption[];
  selectedId: string | null;
  /** Server-side grading outcome; null while unanswered or check in flight */
  result: QuizCheckResult | null;
  /** True while the answer is being checked against the BE */
  checking?: boolean;
  /** Rehydrated question (resumed lesson) — render immediately without re-streaming */
  instant?: boolean;
  onSelect: (id: string) => void;
  onReveal?: () => void; // fired when the question finishes streaming (options appear)
  /** A reviewer reading an answer afterwards (Hiring), never a learner: also marks the correct option. */
  revealCorrect?: boolean;
  /** Smaller type and spacing, to sit among a screen's other small text (Hiring). */
  compact?: boolean;
}

const QuizCard: React.FC<QuizCardProps> = ({
  question,
  options,
  selectedId,
  result,
  checking = false,
  instant = false,
  onSelect,
  onReveal,
  revealCorrect = false,
  compact = false
}) => {
  const [questionStreamed, setQuestionStreamed] = useState(instant);
  const answered = selectedId !== null;

  const stateOf = (o: QuizOption): OptionState => {
    if (!result) return 'default';
    if (revealCorrect && o.id === result.correctOptionId) return 'correct';
    // Reveal only the learner's OWN choice — right (green) or wrong (red). When
    // they're wrong the correct answer stays hidden; they just see what they
    // picked and why it's wrong.
    if (o.id === selectedId) return result.selectedCorrect ? 'correct' : 'incorrect';
    return 'default';
  };

  // Feedback for the learner's own answer only (never reveals the correct
  // option). Index by position in the served options — ids are opaque.
  const selectedIdx = selectedId !== null ? options.findIndex((o) => o.id === selectedId) : -1;
  const selectedReasoning = result && selectedIdx >= 0 ? result.optionReasonings?.[selectedIdx] || '' : '';
  // Withheld (retake-mode wrong answer): the BE sent only the chosen option's
  // rationale. Otherwise: when correct, fall back to the question explainer for
  // older questions with no per-option reasonings; when wrong, only the picked
  // option's reasoning is shown.
  const feedbackBody = result
    ? result.withheld
      ? result.chosenReasoning || ''
      : result.selectedCorrect
        ? selectedReasoning || result.explanation || ''
        : selectedReasoning || result.chosenReasoning || ''
    : '';

  return (
    <>
      {/* Question (streams in like an Elliott message; instant on resume) */}
      <Box
        sx={{
          bgcolor: FIELD_BG,
          px: compact ? '12px' : '16px',
          py: compact ? '8px' : '12px',
          borderRadius: '4px',
          width: '100%'
        }}
      >
        <StreamingText
          paragraphs={[question]}
          sx={compact ? { ...messageBodySx, fontSize: '13px', lineHeight: '19px' } : messageBodySx}
          enabled={!instant}
          onDone={() => {
            setQuestionStreamed(true);
            if (!instant) onReveal?.();
          }}
        />
      </Box>

      {/* Options */}
      {questionStreamed && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? '6px' : '8px', width: '100%', ...fadeIn }}>
          {options.map((o, idx) => {
            const state = stateOf(o);
            const highlighted = state !== 'default';
            const accent = state === 'correct' ? CORRECT_ACCENT : INCORRECT_ACCENT;
            const pendingSelected = answered && !result && o.id === selectedId;
            // Per-option rationale, aligned to the served order by index.
            // Rendered only once the question is SOLVED — on a wrong answer the
            // other options' rationales would give the correct one away.
            const reasoning = result && result.selectedCorrect ? result.optionReasonings?.[idx] || '' : '';
            return (
              <Box
                key={o.id}
                data-testid="quiz-option"
                onClick={answered || checking ? undefined : () => onSelect(o.id)}
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  gap: compact ? '8px' : '12px',
                  px: compact ? '10px' : '12px',
                  py: compact ? '6px' : '10px',
                  borderRadius: '4px',
                  width: '100%',
                  cursor: answered || checking ? 'default' : 'pointer',
                  bgcolor: state === 'correct' ? CORRECT_BG : state === 'incorrect' ? INCORRECT_BG : FIELD_BG,
                  border: highlighted ? 'none' : `1px solid ${pendingSelected ? t.borderContrast : t.borderDim}`,
                  borderLeft: highlighted
                    ? `4px solid ${accent}`
                    : `1px solid ${pendingSelected ? t.borderContrast : t.borderDim}`,
                  '&:hover': answered || checking ? undefined : { borderColor: t.borderContrast }
                }}
              >
                {state === 'correct' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      background: 'linear-gradient(90deg, rgba(0,229,106,0.2) 0%, rgba(6,26,13,0) 100%)'
                    }}
                  />
                )}
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex' }}>
                  <Indicator state={state} size={compact ? 16 : 24} />
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontSize: compact ? 12.5 : 14,
                      lineHeight: compact ? '18px' : '20px',
                      fontWeight: highlighted ? 600 : 500,
                      color: highlighted ? '#fff' : OPTION_TEXT
                    }}
                  >
                    {o.label}
                  </Typography>
                  {reasoning && (
                    <Typography
                      sx={{ fontSize: 12.5, lineHeight: '17px', color: EXPLAINER_TEXT, whiteSpace: 'pre-wrap' }}
                    >
                      {reasoning}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Grading in flight (server round-trip) */}
      {checking && (
        <Box sx={{ px: '4px', ...fadeIn }}>
          <TypingDots />
        </Box>
      )}

      {/* Feedback for the learner's OWN answer — never reveals the correct
          option. In withheld (retake) mode the body is the BE-sent
          chosenReasoning; the wrong-answer question returns later through
          Improve Performance (L-1). */}
      {result && feedbackBody && (
        <Box
          data-testid="quiz-feedback"
          sx={{
            bgcolor: result.selectedCorrect ? CORRECT_BG : INCORRECT_BG,
            borderLeft: `3px solid ${result.selectedCorrect ? CORRECT_ACCENT : INCORRECT_ACCENT}`,
            borderRadius: '4px',
            px: '12px',
            pt: '22px',
            pb: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            width: '100%',
            ...fadeIn
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: '16px', color: '#fff' }}>
            {result.selectedCorrect ? 'Why this is correct' : 'Why this is incorrect'}
          </Typography>
          <Typography
            sx={{ fontSize: 14, fontWeight: 400, lineHeight: '18px', color: EXPLAINER_TEXT, whiteSpace: 'pre-wrap' }}
          >
            {feedbackBody}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default QuizCard;
